import { globalCountriesInfo } from '../../../shared/ui/CountrySelect/countryCatalog';
import { AUTH_ERROR_CODE, OTP_LENGTH } from '../constants';

const NAME_MAX = 100;
const PHONE_DIGITS = 10;
const INDIA = 'IN';
const INDIA_DIAL_DIGITS = '91';

/**
 * Reduce a phone field to the 10-digit mobile number the login API expects.
 * A leading 91 country code is stripped. Longer digit strings keep the last 10.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizePhone(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) {
    return digits.slice(2);
  }
  if (digits.length > 10) {
    return digits.slice(-10);
  }
  return digits;
}

/**
 * Country whose dial code starts these digits; the longest code wins. For a code
 * several countries share (+1), the current pick is kept when it is one of them,
 * otherwise the first listed.
 *
 * @param {string} digits
 * @param {string} currentCountry
 * @returns {import('../../../shared/ui/CountrySelect/countryCatalog').Country | null}
 */
function countryFromDialDigits(digits, currentCountry) {
  const matches = globalCountriesInfo.filter((country) => digits.startsWith(country.phoneCode.slice(1)));
  if (matches.length === 0) return null;
  const longest = Math.max(...matches.map((country) => country.phoneCode.length));
  const best = matches.filter((country) => country.phoneCode.length === longest);
  return best.find((country) => country.countryCode === currentCountry) || best[0];
}

/**
 * Split a mobile number field value, typed or filled by the browser, into the
 * country picker value and up to 10 national digits.
 * - "+<dial code> …" (Chrome autofill, paste): the picker moves to that country.
 * - 12 digits starting 91: India. 11 digits starting 0: the trunk 0 is dropped.
 * - Anything else keeps the first 10 digits and the current country, as typing does.
 *
 * @param {unknown} value
 * @param {string} currentCountry - ISO code picked now, e.g. "IN"
 * @returns {{ phone: string, countryCode: string }}
 */
export function parsePhoneInput(value, currentCountry) {
  const raw = String(value ?? '').trim();
  const digits = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) {
    const country = countryFromDialDigits(digits, currentCountry);
    if (country) {
      const national = digits.slice(country.phoneCode.length - 1).replace(/^0/, '');
      return { phone: national.slice(0, PHONE_DIGITS), countryCode: country.countryCode };
    }
  }
  if (digits.length === 12 && digits.startsWith(INDIA_DIAL_DIGITS)) {
    return { phone: digits.slice(INDIA_DIAL_DIGITS.length), countryCode: INDIA };
  }
  if (digits.length === PHONE_DIGITS + 1 && digits.startsWith('0')) {
    return { phone: digits.slice(1), countryCode: currentCountry };
  }
  return { phone: digits.slice(0, PHONE_DIGITS), countryCode: currentCountry };
}

/**
 * @param {unknown} value
 * @returns {{ ok: true, value: string } | { ok: false, errorCode: string }}
 */
export function validatePhone(value) {
  const phone = normalizePhone(value);
  if (!/^\d{10}$/.test(phone)) {
    return { ok: false, errorCode: AUTH_ERROR_CODE.INVALID_PHONE };
  }
  return { ok: true, value: phone };
}

/**
 * @param {unknown} value
 * @returns {{ ok: true, value: string } | { ok: false, errorCode: string }}
 */
export function validateOtp(value) {
  const otp = String(value ?? '').replace(/\D/g, '');
  if (otp.length !== OTP_LENGTH) {
    return { ok: false, errorCode: AUTH_ERROR_CODE.INVALID_OTP };
  }
  return { ok: true, value: otp };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Basic shape check: something@domain.tld. The server still decides.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidEmail(value) {
  return EMAIL_PATTERN.test(String(value ?? '').trim());
}

/**
 * @param {{ firstName?: unknown, lastName?: unknown }} [profile]
 * @returns {{ ok: true, value: { firstName: string, lastName: string } } | { ok: false, errorCode: string }}
 */
export function validateProfile(profile = {}) {
  const firstName = String(profile.firstName ?? '').trim();
  const lastName = String(profile.lastName ?? '').trim();
  if (!firstName || firstName.length > NAME_MAX || lastName.length > NAME_MAX) {
    return { ok: false, errorCode: AUTH_ERROR_CODE.PROFILE_INCOMPLETE };
  }
  return { ok: true, value: { firstName, lastName } };
}
